import React, { Component, createRef } from 'react';
import moment from 'moment';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils';
import { connect } from 'react-redux';
import { Modal, Row, Col, Button } from 'react-bootstrap';
import {
  reduxForm,
  Field,
  formValueSelector,
  getFormSyncErrors,
} from 'redux-form';
import { FormattedMessage } from 'react-intl';
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import { renderDateField } from '../../utils/redux-form-helper';

import { moveInMessages, moveOutMessages } from './messages.js';
import validate from './validate';
import MoveInCorrectionImpacts, {
  IMPACTS_COUNT_ID,
} from './MoveInCorrectionImpacts';
import MoveOutCorrectionImpacts, {
  MOVE_OUT_IMPACTS_COUNT_ID,
} from './MoveOutCorrectionImpacts';
import CorrectedLeaseUpload from './CorrectedLeaseUpload';
import {
  CORRECTION_BODY_STYLE,
  CorrectionPreviewCacheReset,
  IMPACTS_SCROLL_STYLE,
  toPreviewDate,
} from './correctionImpacts';
import type { DateFieldValue, PreviewStatus } from './correctionImpacts';

const MOVE_IN_FORM = 'editMoveInDateForm';
const MOVE_OUT_FORM = 'editMoveOutDateForm';

type MoveType = 'moveIn' | 'moveOut';

// A future day cannot be a Move-In or Move-Out Date. Disabled in the picker when the
// household-mi-mo-date-changes flag is on, mirroring the Property Move-In modal.
const isFutureDate = (date: Object) => moment(date).isAfter(moment(), 'day');
const isSelectableDate = (current: Object) => !isFutureDate(current);

type Props = {
  show: boolean,
  dismiss: Function,
  handleSubmit: Function,
  onSubmit: Function,
  pristine: boolean,
  valid: boolean,
  submitting: boolean,
  reset: Function,
  clearSubmitErrors: Function,
  dirty: boolean,
  initialValues: Object,
  moveType: MoveType,
  additionalValidation?: Function,
  isHouseholdMiMoDateChangesEnabled?: boolean,
  // Date correction preview (household-mi-mo-date-changes) only.
  residentId?: string,
  organizationId?: string,
  propertyId?: string,
  /** The Move-In Date being corrected, YYYY-MM-DD. */
  currentMoveInDate?: ?string,
  /** The Move-Out Date being corrected, YYYY-MM-DD. */
  currentMoveOutDate?: ?string,
  /**
   * The move-in lease of the stay being corrected, set when it was opened from the Unit History;
   * without it the server corrects the current stay.
   */
  stayLeaseId?: ?string,
  /** The unit of that stay, named in the title. */
  unitLabel?: ?string,
  proposedMoveDate?: DateFieldValue,
  hasSyncErrors?: boolean,
  // Legacy Move-Out modal (flag off): the RD Vacate banner.
  isRDFloor?: boolean,
  intl?: Object,
};

type State = {
  showCancelConfirmation: boolean,
  previewStatus: PreviewStatus,
  /** The YYYY-MM-DD date previewStatus answers. */
  previewDate: ?string,
  /** Records the impact list shows (0 unless the preview is valid). */
  previewRecordCount: number,
  /**
   * The Move-In correction's Upload Corrected Lease dialog (ENG-33327) replaces this modal while
   * open ('upload'), never stacked on it.
   */
  view: 'form' | 'upload',
  /** The Corrected Lease sent with the save; a File isn't serializable, so not a form field. */
  heldFile: ?File,
};

const INITIAL_UPLOAD_STATE = { view: 'form', heldFile: null };

export class EditMoveDateModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showCancelConfirmation: false,
      previewStatus: 'idle',
      previewDate: null,
      previewRecordCount: 0,
      ...INITIAL_UPLOAD_STATE,
    };
  }

  /** The Active Lease row's Upload / Edit uploaded button. */
  uploadButtonRef = createRef();

  /** Back from the upload dialog: focus the row's button once this modal has shown again. */
  focusUploadButtonOnEnter = false;

  // Everything gated here applies only with the household-mi-mo-date-changes flag on (the Move-In
  // and the prior resident's Move-Out correction); with it off both modals keep their legacy path.
  get correctionFlagOn(): boolean {
    return Boolean(this.props.isHouseholdMiMoDateChangesEnabled);
  }

  get correctedLeaseUploadEnabled(): boolean {
    return this.correctionFlagOn && this.props.moveType === 'moveIn';
  }

  componentDidUpdate(prevProps: Props) {
    // A server error from a prior save must not linger when the modal reopens.
    if (this.correctionFlagOn && this.props.show && !prevProps.show) {
      this.props.clearSubmitErrors();
    }
    // Opened, closed or saved: no upload dialog and no file carried over.
    if (this.props.show !== prevProps.show) {
      this.setState(INITIAL_UPLOAD_STATE);
    }
  }

  onPreviewStatusChange = (
    previewStatus: PreviewStatus,
    previewDate: ?string,
    previewRecordCount: number,
  ) => {
    this.setState({ previewStatus, previewDate, previewRecordCount });
  };

  messages =
    this.props.moveType === 'moveIn' ? moveInMessages : moveOutMessages;

  cancelConfirmationToggle = (visible: boolean) => {
    this.setState({
      showCancelConfirmation: visible,
    });
  };

  onClose = () => {
    const { dirty, reset, dismiss, clearSubmitErrors } = this.props;
    const { showCancelConfirmation } = this.state;
    // A held Corrected Lease is an unsaved change too (UL6).
    const isDirty = dirty || Boolean(this.state.heldFile);

    if (isDirty && !showCancelConfirmation) {
      this.cancelConfirmationToggle(true);
      return;
    } else if (dirty && showCancelConfirmation) {
      reset();
    }

    // A server error from a failed save must not linger when reopened.
    if (this.correctionFlagOn) {
      clearSubmitErrors();
    }

    this.setState(INITIAL_UPLOAD_STATE);
    this.cancelConfirmationToggle(false);
    dismiss();
  };

  openCorrectedLeaseUpload = () => {
    this.setState({ view: 'upload' });
  };

  closeCorrectedLeaseUpload = () => {
    this.focusUploadButtonOnEnter = true;
    this.setState({ view: 'form' });
  };

  saveCorrectedLeaseUpload = (file: ?File) => {
    this.focusUploadButtonOnEnter = true;
    this.setState({ view: 'form', heldFile: file });
  };

  // The valid preview no longer offers the upload (UL8): the file would be refused at save.
  dropCorrectedLease = () => {
    if (this.state.heldFile) {
      this.setState({ heldFile: null });
    }
  };

  onModalEntered = () => {
    if (this.focusUploadButtonOnEnter) {
      this.focusUploadButtonOnEnter = false;
      this.uploadButtonRef.current?.focus();
    }
  };

  // The Move-In correction sends the held Corrected Lease with the date.
  submitWithCorrectedLease = (values: Object) =>
    this.props.onSubmit(values, { correctedLease: this.state.heldFile });

  renderModalHeader = () => {
    return (
      <Modal.Header closeButton>
        <i className="icon et-pencil" />
        <Modal.Title componentClass="h1">
          {this.correctionFlagOn && this.props.unitLabel ? (
            <FormattedMessage
              {...moveInMessages.titleForUnit}
              values={{ unitLabel: this.props.unitLabel }}
            />
          ) : (
            <FormattedMessage {...this.messages.title} />
          )}
        </Modal.Title>
      </Modal.Header>
    );
  };

  renderModalFooter = () => {
    const {
      pristine,
      valid,
      submitting,
      handleSubmit,
      hasSyncErrors,
      proposedMoveDate,
    } = this.props;
    const { showCancelConfirmation, previewStatus, previewDate } = this.state;
    // Under the flag Save needs a changed date whose preview loaded as valid: it stays off while the
    // preview loads, when the server rejects the date, and when the preview failed to load. The
    // status must answer the date in the field now, not one edited since.
    const submitEnabled = this.correctionFlagOn
      ? !submitting &&
        !pristine &&
        !hasSyncErrors &&
        previewStatus === 'valid' &&
        previewDate === toPreviewDate(proposedMoveDate)
      : pristine || (!pristine && valid);

    return (
      <Modal.Footer className={showCancelConfirmation ? 'disabled' : ''}>
        <Row>
          <Col xs={6}>
            <Button
              bsStyle="default"
              className="pull-right"
              onClick={this.onClose}
            >
              Cancel
            </Button>
          </Col>
          <Col xs={6}>
            <Button
              bsStyle="primary"
              className="pull-left"
              type="submit"
              onClick={
                this.correctedLeaseUploadEnabled
                  ? handleSubmit(this.submitWithCorrectedLease)
                  : handleSubmit
              }
              disabled={!submitEnabled}
            >
              Save Changes
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    );
  };

  renderCurrentDate = () => {
    const { moveType, currentMoveInDate, currentMoveOutDate } = this.props;
    const currentDate =
      moveType === 'moveIn' ? currentMoveInDate : currentMoveOutDate;
    if (!this.correctionFlagOn || !currentDate) {
      return null;
    }
    return (
      <Typography
        variant="bodySmall"
        color="text.secondary"
        component="div"
        data-test={
          moveType === 'moveIn' ? 'mi-current-date' : 'mo-current-date'
        }
      >
        <FormattedMessage
          {...this.messages.currentDate}
          values={{
            date: formatDateDisplayUTC(currentDate, 'MM/DD/YYYY'),
          }}
        />
      </Typography>
    );
  };

  renderImpactsList = () => {
    const {
      moveType,
      residentId,
      organizationId,
      propertyId,
      currentMoveInDate,
      currentMoveOutDate,
      stayLeaseId,
      proposedMoveDate,
      hasSyncErrors,
    } = this.props;
    if (moveType === 'moveIn') {
      return (
        <MoveInCorrectionImpacts
          residentId={residentId ?? ''}
          organizationId={organizationId ?? ''}
          propertyId={propertyId ?? ''}
          proposedDate={proposedMoveDate}
          currentMoveInDate={currentMoveInDate}
          stayLeaseId={stayLeaseId ?? null}
          clientValid={!hasSyncErrors}
          onStatusChange={this.onPreviewStatusChange}
          correctedLease={this.state.heldFile}
          onUploadCorrectedLease={this.openCorrectedLeaseUpload}
          onCorrectedLeaseNotOffered={this.dropCorrectedLease}
          uploadButtonRef={this.uploadButtonRef}
          keepPreviewCache={this.state.view === 'upload'}
        />
      );
    }
    return (
      <MoveOutCorrectionImpacts
        residentId={residentId ?? ''}
        organizationId={organizationId ?? ''}
        propertyId={propertyId ?? ''}
        proposedDate={proposedMoveDate}
        currentMoveOutDate={currentMoveOutDate}
        clientValid={!hasSyncErrors}
        onStatusChange={this.onPreviewStatusChange}
      />
    );
  };

  renderImpacts = () => {
    if (!this.correctionFlagOn) {
      return null;
    }
    const isMoveIn = this.props.moveType === 'moveIn';
    // The one scrolling box of the flag-on modal: only the affected-record list scrolls, and only
    // when it outgrows the viewport. Modal.Body's own scroll (et-custom.css) is turned off below,
    // so the date field has no scrolling ancestor (its calendar pops out unclipped) and the Cancel
    // confirmation pinned to the top of Modal.Body stays in view. Its rows hold no focusable
    // control except the Active Lease row's Upload button (Move-In, ENG-33327), which may sit
    // below the fold, so while a list is shown the box itself takes focus, named by the record
    // count, for keyboard users to scroll it.
    const hasImpactList = this.state.previewRecordCount > 0;
    return (
      <div
        data-test={isMoveIn ? 'mi-impacts-scroll' : 'mo-impacts-scroll'}
        {...(hasImpactList
          ? {
              tabIndex: 0,
              role: 'region',
              'aria-labelledby': isMoveIn
                ? IMPACTS_COUNT_ID
                : MOVE_OUT_IMPACTS_COUNT_ID,
            }
          : {})}
        style={IMPACTS_SCROLL_STYLE}
      >
        {this.renderImpactsList()}
      </div>
    );
  };

  renderModalFormBody = () => {
    return (
      <Row className="scrollable-modal__content">
        <div className="container-fluid">
          <Row>
            <Col md={5} xs={12}>
              <label>
                <FormattedMessage {...this.messages.dateLabel} />
              </label>
              {this.renderCurrentDate()}
            </Col>
            <Col md={7} xs={12}>
              <Field
                name="editMoveDate"
                component={renderDateField}
                bsSize="md"
                {...(this.correctionFlagOn
                  ? { isValidDate: isSelectableDate }
                  : {})}
              />
            </Col>
          </Row>
          {this.renderImpacts()}
        </div>
      </Row>
    );
  };

  renderModalCancelBody = () => {
    return (
      <div className="modal-confirm">
        <Row>
          <Col xs={12}>
            <h1>Are you sure you want to leave without saving?</h1>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={6}>
            <Button
              bsStyle="primary"
              className="pull-right"
              onClick={this.onClose}
            >
              Yes
            </Button>
          </Col>
          <Col xs={12} sm={6}>
            <Button
              bsStyle="default"
              className="pull-left"
              onClick={() => this.cancelConfirmationToggle(false)}
            >
              No
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  renderWarningBanner = () => {
    const { moveType, isRDFloor, intl } = this.props;

    // Under the flag the preview lists what happens to the Vacate cert instead.
    if (moveType !== 'moveOut' || !isRDFloor || this.correctionFlagOn) {
      return null;
    }

    return (
      <Box padding={2}>
        <Banner
          BoxProps={{ sx: { maxWidth: '100%', width: '100%', mb: 2 } }}
          TypographyProps={{
            noWrap: false,
          }}
          text={intl.formatMessage(moveOutMessages.rdWarning)}
          color="warning"
        />
      </Box>
    );
  };

  render() {
    const { show } = this.props;
    const { showCancelConfirmation, view, heldFile } = this.state;
    const isUploadView = view === 'upload';

    return (
      <form>
        <Modal
          className="edit-move-date"
          bsSize="sm"
          // The app's medium modal (et-custom.css .modal-sm.modal-md, 580px) fits the review
          // banner on one line and gives the impact rows room next to their chips.
          dialogClassName={this.correctionFlagOn ? 'modal-md' : undefined}
          backdrop
          // Hidden, not closed, while the Upload Corrected Lease dialog is open (no stacked
          // modals): the parent's `show` doesn't change, so the form and its date survive, and the
          // cached preview lists the impacts again without a request when it shows again.
          show={show && !isUploadView}
          onHide={() => this.onClose()}
          onEntered={this.onModalEntered}
          // While hidden for the upload dialog (a component-library modal, outside react-bootstrap's
          // modal stack) this modal's focus listener stays on through its closing fade: it must not
          // pull focus back from the dialog. Focus moves to the dialog, not back to the page.
          enforceFocus={!isUploadView}
          restoreFocus={!isUploadView}
        >
          {this.renderModalHeader()}
          <Modal.Body
            style={this.correctionFlagOn ? CORRECTION_BODY_STYLE : undefined}
          >
            {this.renderWarningBanner()}
            {this.renderModalFormBody()}
            {showCancelConfirmation && this.renderModalCancelBody()}
          </Modal.Body>
          {this.renderModalFooter()}
        </Modal>
        {this.correctedLeaseUploadEnabled && (
          <>
            <CorrectedLeaseUpload
              show={show && isUploadView}
              heldFile={heldFile}
              onCancel={this.closeCorrectedLeaseUpload}
              onSave={this.saveCorrectedLeaseUpload}
            />
            {/* The impact list keeps its previews while hidden for the dialog: drop them if the
                page goes away meanwhile. */}
            <CorrectionPreviewCacheReset queryKeyPrefix="moveInDateCorrectionPreview" />
          </>
        )}
      </form>
    );
  }
}

export const mapStateToProps = (
  { form }: Object,
  { initialValues }: Object,
): Object => {
  return {
    initialValues,
  };
};

// The date in the field and whether it passes the form's rules, for the correction preview.
const mapCorrectionStateToProps = (form: string) => {
  const selectMoveDate = formValueSelector(form);
  const selectSyncErrors = getFormSyncErrors(form);
  return (state: Object, ownProps: Object): Object => ({
    ...mapStateToProps(state, ownProps),
    proposedMoveDate: selectMoveDate(state, 'editMoveDate'),
    hasSyncErrors: Boolean(selectSyncErrors(state)?.editMoveDate),
  });
};

export const mapMoveInStateToProps: (Object, Object) => Object =
  mapCorrectionStateToProps(MOVE_IN_FORM);
export const mapMoveOutStateToProps: (Object, Object) => Object =
  mapCorrectionStateToProps(MOVE_OUT_FORM);

export const EditMoveInDateModal = connect(mapMoveInStateToProps)(
  reduxForm({
    form: MOVE_IN_FORM,
    enableReinitialize: true,
    validate,
  })(EditMoveDateModal),
);

export const EditMoveOutDateModal = connect(mapMoveOutStateToProps)(
  reduxForm({
    form: MOVE_OUT_FORM,
    enableReinitialize: true,
    validate,
  })(EditMoveDateModal),
);
